import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button } from "@heroui/react";
import { RiAddCircleLine } from "react-icons/ri";
import type { TimeOffGranularity } from "@/server-types";
import { LimeInputLabel } from "@/Components/NextBase/LimeInputLabel";
import { formatDuration } from "../TimeOff/form/timeOffFormat";
import { fallbackTimeOffEmoji } from "./timeOffEmojis";
import { resolveTimeOffTypeName } from "./timeOffTypeDisplayName";

/** Matches the calendar column's fallback for a type with no colour of its own. */
export const UNTYPED_TYPE_COLOR = "#969BA9";

/** Everything a tile needs. Callers pass their own wider row through. */
export type TimeOffTileType = {
  id: number;
  name: string | null;
  systemKey: string | null;
  color: string | null;
  emoji: string | null;
  granularity: TimeOffGranularity;
  defaultDurationMins: number | null;
};

/** Two sideways-scrolling rows of tiles by granularity, each ending in its own "Nova vrsta" card. */
export function TimeOffTypeTiles<T extends TimeOffTileType>({
  timeTypes,
  dayTypes,
  showTimeGroup,
  showDayGroup,
  value,
  onChange,
  onAddType,
  error,
}: {
  timeTypes: T[];
  dayTypes: T[];
  /** False where hour-level types are not offered at all. */
  showTimeGroup: boolean;
  /** False where day-level types are not offered at all. */
  showDayGroup: boolean;
  value: number | null;
  onChange: (type: T) => void;
  /** Omitted for a caller who may not create types, which hides the cards. */
  onAddType?: (granularity: TimeOffGranularity) => void;
  error?: string;
}) {
  // A group with nothing in it still shows while its card can fill it.
  const showTimes =
    showTimeGroup && (timeTypes.length > 0 || onAddType != null);
  const showDays = showDayGroup && (dayTypes.length > 0 || onAddType != null);

  return (
    <div className="flex flex-col gap-3">
      {showTimes && (
        <TileGroup
          label={t`Urne odsotnosti`}
          granularity="TIME"
          types={timeTypes}
          value={value}
          onChange={onChange}
          onAddType={onAddType}
          subtitle={(type) =>
            type.defaultDurationMins == null
              ? null
              : formatDuration(type.defaultDurationMins)
          }
        />
      )}
      {showDays && (
        <TileGroup
          label={t`Dnevne odsotnosti`}
          granularity="DAY"
          types={dayTypes}
          value={value}
          onChange={onChange}
          onAddType={onAddType}
          // Part days are allowed, so a day-level type is whole days only by default.
          subtitle={() => <Trans>privzeto ves dan</Trans>}
        />
      )}
      {!showTimes && !showDays && (
        <p className="text-default-400 text-tiny">
          <Trans>Ni vrst odsotnosti.</Trans>
        </p>
      )}
      {error && <p className="text-danger text-tiny">{error}</p>}
    </div>
  );
}

function TileGroup<T extends TimeOffTileType>({
  label,
  granularity,
  types,
  value,
  onChange,
  onAddType,
  subtitle,
}: {
  label: string;
  granularity: TimeOffGranularity;
  types: T[];
  value: number | null;
  onChange: (type: T) => void;
  onAddType?: (granularity: TimeOffGranularity) => void;
  subtitle: (type: T) => React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <LimeInputLabel label={label} />
      <div className="flex gap-2 overflow-x-auto pb-1">
        {types.map((type) => {
          const isSelected = type.id === value;
          const color = type.color ?? UNTYPED_TYPE_COLOR;
          const line = subtitle(type);

          return (
            <Button
              key={type.id}
              type="button"
              variant="bordered"
              aria-pressed={isSelected}
              data-identifier={`time-off-type-${type.granularity}`}
              data-type-id={type.id}
              onPress={() => onChange(type)}
              className="h-auto min-h-[76px] w-[140px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border-2 px-2 py-2"
              style={
                isSelected
                  ? { borderColor: color, backgroundColor: `${color}1a` }
                  : undefined
              }
            >
              <span className="text-xl leading-none">
                {type.emoji ?? fallbackTimeOffEmoji(type.granularity)}
              </span>
              <span className="w-full truncate text-center font-semibold text-sm">
                {resolveTimeOffTypeName(type)}
              </span>
              {line != null && (
                <span className="w-full truncate text-center font-normal text-default-400 text-tiny">
                  {line}
                </span>
              )}
            </Button>
          );
        })}
        {onAddType && (
          <Button
            type="button"
            variant="bordered"
            data-identifier={`time-off-type-add-${granularity}`}
            onPress={() => onAddType(granularity)}
            className="h-auto min-h-[76px] w-[140px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border-2 border-primary border-dashed px-2 py-2 text-primary"
          >
            <RiAddCircleLine className="text-xl" />
            <span className="w-full truncate text-center font-semibold text-sm">
              <Trans>Nova vrsta</Trans>
            </span>
          </Button>
        )}
      </div>
    </div>
  );
}
